import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import {
  getAllProductSurplusTransportLCA,
  addProductSurplusTransportLCA,
  updateProductSurplusTransportLCA,
  deleteProductSurplusTransportLCA,
} from "../../../../../../apis/Product/SurplusTransportApi";
import {
  getProductSurplusTransportDisposalMethodDropdown,
  getProductSurplusTransportCountryDropdown,
} from "../../../../../../apis/Dropdown";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";
import { IconButton } from "@mui/material";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import EditableResultDropdownAutocomplete from "../../../../../../components/EditableResultDropdownAutocomplete";
import { TableCompareTrigger } from "../../../components/lcaComparison";

// Impact categories configuration - All 16 categories
const IMPACT_CATEGORIES = [
  { name: "Climate change", unit: "kg CO₂-eq", key: "global_warming" },
  { name: "Ozone depletion", unit: "kg CFC-11-eq", key: "ozone_depletion" },
  {
    name: "Photochemical ozone formation",
    unit: "kg NMVOC-eq",
    key: "smog",
  },
  { name: "Acidification", unit: "mol H⁺-eq", key: "acidification" },
  {
    name: "Eutrophication, terrestrial",
    unit: "mol N-eq",
    key: "eutrophication_terrestrial",
  },
  {
    name: "Eutrophication, freshwater",
    unit: "kg P-eq",
    key: "eutrophication_freshwater",
  },
  {
    name: "Eutrophication, marine",
    unit: "kg N-eq",
    key: "eutrophication_marine",
  },
  {
    name: "Human toxicity, cancer effects",
    unit: "CTUh",
    key: "carcinogenics",
  },
  {
    name: "Human toxicity, non-cancer effects",
    unit: "CTUh",
    key: "non_carcinogenics",
  },
  {
    name: "Particulate matter",
    unit: "disease incidence",
    key: "respiratory_effects",
  },
  {
    name: "Ecotoxicity, freshwater",
    unit: "CTUe",
    key: "ecotoxicity",
  },
  {
    name: "Resource use, energy carriers",
    unit: "MJ",
    key: "fossil_fuel_depletion",
  },
  { name: "Land use", unit: "Pt", key: "land_use" },
  {
    name: "Water use",
    unit: "m³ world-eq deprived",
    key: "water_use",
  },
  {
    name: "Resource use, mineral and metals",
    unit: "kg Sb-eq",
    key: "resource_use_minerals_metals",
  },
  {
    name: "Ionising radiation, human health",
    unit: "kBq U235-eq",
    key: "ionising_radiation",
  },
];

const ProdSurplusTransportTable = ({ productId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [unitMaterials, setUnitMaterials] = useState({});
  const [disposalMethodDropdown, setDisposalMethodDropdown] = useState([]);
  const [countryDropdown, setCountryDropdown] = useState([]);
  const [currentImpactCategoryIndex, setCurrentImpactCategoryIndex] =
    useState(0);

  // Fetch dropdown data
  useEffect(() => {
    getProductSurplusTransportDisposalMethodDropdown({
      setDropdown: setDisposalMethodDropdown,
    });
    getProductSurplusTransportCountryDropdown({
      setDropdown: setCountryDropdown,
    });
  }, []);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    if (productId) {
      getAllProductSurplusTransportLCA({
        productId,
        setObjects: setData,
      });
    }
  }, [productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resolveDisposalMethodUnit = useCallback(
    (rowData) => {
      const disposalMethodCode =
        typeof rowData?.disposal_method === "string"
          ? rowData.disposal_method
          : rowData?.disposal_method?.value || rowData?.disposal_method;
      if (!disposalMethodCode || !disposalMethodDropdown.length) {
        return null;
      }
      const selectedDisposalMethod = disposalMethodDropdown.find(
        (option) =>
          !option.isCategory &&
          (option.code === disposalMethodCode ||
            option.value === disposalMethodCode ||
            option.name === disposalMethodCode)
      );
      return selectedDisposalMethod?.unit || null;
    },
    [disposalMethodDropdown]
  );

  /** Prefer persisted API field, then dropdown lookup, then in-memory add-row state */
  const resolveQtyUnitDisplay = useCallback(
    (rowData, rowId) => {
      const stored = rowData?.qty_unit;
      if (stored) {
        return stored;
      }
      return (
        resolveDisposalMethodUnit(rowData) ||
        (rowId != null ? unitMaterials[rowId] : null) ||
        "xx"
      );
    },
    [resolveDisposalMethodUnit, unitMaterials]
  );

  const clearNewRowQtyUnit = useCallback(() => {
    setUnitMaterials((prev) => {
      const next = { ...prev };
      delete next.new;
      return next;
    });
  }, []);

  // Convert treatment_country code to name when countryDropdown is loaded
  useEffect(() => {
    if (data.length > 0 && countryDropdown.length > 0) {
      setData((prevData) => {
        let hasChanges = false;
        const convertedData = prevData.map((item) => {
          if (item.treatment_country && typeof item.treatment_country === "string") {
            // Check if it's a code (2-3 letter code like "AR") - codes are typically 2-3 characters
            // If it's already a name (longer string), don't convert
            if (item.treatment_country.length <= 3) {
              const foundCountry = countryDropdown.find(
                (option) => option.code === item.treatment_country
              );
              if (foundCountry && item.treatment_country !== foundCountry.name) {
                hasChanges = true;
                return {
                  ...item,
                  treatment_country: foundCountry.name, // Display name instead of code
                };
              }
            }
          }
          return item;
        });
        return hasChanges ? convertedData : prevData;
      });
    }
  }, [data, countryDropdown]);

  useEffect(() => {
    setUnitMaterials((prev) => {
      const next = { ...prev };
      data.forEach((row) => {
        const u = row.qty_unit || resolveDisposalMethodUnit(row);
        if (u) {
          next[row.id] = u;
        }
      });
      return next;
    });
  }, [data, disposalMethodDropdown, resolveDisposalMethodUnit]);

  // Navigation handlers for impact categories
  const handlePreviousImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev > 0 ? prev - 1 : IMPACT_CATEGORIES.length - 1
    );
  }, []);

  const handleNextImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev < IMPACT_CATEGORIES.length - 1 ? prev + 1 : 0
    );
  }, []);

  const currentImpactCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
  const currentImpactKey = currentImpactCategory.key;

  const columns = useMemo(
    () => [
      {
        accessor: "surplus_resource",
        header: t("SURPLUS_RESOURCE") || "Surplus Resource",
        type: "text",
        disableSortBy: true,
        width: "200px",
        placeholder: t("USER_TYPES") || "user types...",
      },
      {
        accessor: "disposal_method",
        header: `${t("CURRENT_DISPOSAL_METHOD") || "Current disposal method"}*`,
        type: "dropdown",
        isCategory: true,
        dropdownData: disposalMethodDropdown,
        disableSortBy: true,
        width: "250px",
        unit: (rowId, rowData) =>
          resolveQtyUnitDisplay(rowData, rowId),
      },
      {
        accessor: "qty",
        header: `${t("QTY_FUNC_UNIT") || "Quantity per functional unit"}*`,
        type: "number",
        disableSortBy: true,
        width: "250px",
        placeholder: t("USER_TYPES") || "user types...",
        unit: (rowId, rowData) => resolveQtyUnitDisplay(rowData, rowId),
      },
      {
        accessor: "treatment_country",
        header: t("TREATMENT_COUNTRY") || "Treatment country",
        type: "dropdown",
        dropdownData: countryDropdown,
        disableSortBy: true,
        width: "200px",
        isCategory: false, // No categories for country dropdown
      },
      {
        accessor: currentImpactKey,
        header: (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <IconButton
              size="small"
              onClick={handlePreviousImpactCategory}
              style={{ padding: "4px" }}
            >
              <KeyboardArrowLeft />
            </IconButton>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <span>{currentImpactCategory.name}</span>
              <span style={{ fontSize: "12px", color: "#666" }}>
                {currentImpactCategory.unit}
              </span>
            </div>
            <IconButton
              size="small"
              onClick={handleNextImpactCategory}
              style={{ padding: "4px" }}
            >
              <KeyboardArrowRight />
            </IconButton>
            <TableCompareTrigger />
          </div>
        ),
        type: "custom",
        disableSortBy: true,
        width: "200px",
        optional: true, // Not required - calculated output only
        cell: ({ row }) => {
          const currentCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
          // Backend returns impact categories directly on the object (e.g., global_warming)
          const impactValue =
            row.original[currentCategory.key] || // Direct field from API (e.g., global_warming)
            row.original[`impact_${currentCategory.key}`] || // Fallback for nested structure
            row.original.impact_categories?.[currentCategory.key] ||
            0;
          return <CustomInputComponent value={impactValue} readOnly />;
        },
      },
    ],
    [
      t,
      disposalMethodDropdown,
      countryDropdown,
      unitMaterials,
      currentImpactCategoryIndex,
      currentImpactCategory,
      currentImpactKey,
      handlePreviousImpactCategory,
      handleNextImpactCategory,
      resolveDisposalMethodUnit,
      resolveQtyUnitDisplay,
    ]
  );

  const handleEdit = useCallback(
    (editedItem) => {
      const disposalMethodValue =
        editedItem.disposal_method?.value ||
        editedItem.disposal_method ||
        editedItem.input?.value ||
        editedItem.input;

      const foundDisposalMethod = disposalMethodDropdown?.find(
        (option) =>
          !option.isCategory &&
          (option.value === disposalMethodValue ||
            option.code === disposalMethodValue ||
            option.name === disposalMethodValue)
      );

      const disposalMethodCode = foundDisposalMethod?.code || disposalMethodValue;

      // Treatment country: backend expects code, but dropdown shows name
      // If value is name, find the code; if it's already code, use it
      let treatmentCountryValue = null;
      if (editedItem.treatment_country) {
        const countryValue =
          typeof editedItem.treatment_country === "object"
            ? editedItem.treatment_country?.value || editedItem.treatment_country?.name
            : editedItem.treatment_country;
        // Find country by name or code
        const foundCountry = countryDropdown.find(
          (option) =>
            option.name === countryValue ||
            option.code === countryValue ||
            option.value === countryValue
        );
        treatmentCountryValue = foundCountry?.code || countryValue;
      }

      updateProductSurplusTransportLCA({
        productId,
        surplusTransportId: editedItem.id,
        surplus_resource: editedItem.surplus_resource,
        disposal_method: disposalMethodCode,
        qty: editedItem.qty,
        qty_unit:
          foundDisposalMethod?.unit ?? editedItem.qty_unit ?? null,
        treatment_country: treatmentCountryValue,
        objects: data,
        setObjects: setData,
        callback: fetchData,
        use_swal: true,
      });
    },
    [productId, fetchData, disposalMethodDropdown, countryDropdown, data]
  );

  const handleAdd = useCallback(
    (newItem) => {
      const disposalMethodValue =
        newItem.disposal_method?.value ||
        newItem.disposal_method ||
        newItem.input?.value ||
        newItem.input;

      const foundDisposalMethod = disposalMethodDropdown?.find(
        (option) =>
          !option.isCategory &&
          (option.value === disposalMethodValue ||
            option.code === disposalMethodValue ||
            option.name === disposalMethodValue)
      );

      const disposalMethodCode = foundDisposalMethod?.code || disposalMethodValue;

      // Treatment country: backend expects code, but dropdown shows name
      // If value is name, find the code; if it's already code, use it
      let treatmentCountryValue = null;
      if (newItem.treatment_country) {
        const countryValue =
          typeof newItem.treatment_country === "object"
            ? newItem.treatment_country?.value || newItem.treatment_country?.name
            : newItem.treatment_country;
        // Find country by name or code
        const foundCountry = countryDropdown.find(
          (option) =>
            option.name === countryValue ||
            option.code === countryValue ||
            option.value === countryValue
        );
        treatmentCountryValue = foundCountry?.code || countryValue;
      }

      addProductSurplusTransportLCA({
        productId,
        surplus_resource: newItem.surplus_resource,
        disposal_method: disposalMethodCode,
        qty: newItem.qty,
        qty_unit: foundDisposalMethod?.unit || null,
        treatment_country: treatmentCountryValue,
        callback: () => {
          fetchData();
          clearNewRowQtyUnit();
        },
        use_swal: true,
      });
    },
    [productId, fetchData, disposalMethodDropdown, countryDropdown, clearNewRowQtyUnit]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteProductSurplusTransportLCA({
        productId,
        surplusTransportId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [productId, fetchData]
  );

  const disableRules = useMemo(() => {
    const rules = {};
    IMPACT_CATEGORIES.forEach((category) => {
      rules[category.key] = () => true; // All impact categories are read-only
    });
    return rules;
  }, []);

  // Dynamic placeholders to update unit when disposal method is selected
  const dynamicPlaceholders = useMemo(
    () => ({
      disposal_method: {
        targetField: "qty",
        getPlaceholder: (
          selectedValue,
          dropdownData,
          rowData,
          rowId = "new"
        ) => {
          const selectedDisposalMethod = dropdownData?.find(
            (option) =>
              !option.isCategory &&
              (option.value === selectedValue ||
                option.code === selectedValue ||
                option.name === selectedValue)
          );
          if (selectedDisposalMethod) {
            if (rowId !== "new") {
              setUnitMaterials((prev) => ({
                ...prev,
                [rowId]: selectedDisposalMethod.unit || "xx",
              }));
            } else {
              setUnitMaterials((prev) => ({
                ...prev,
                new: selectedDisposalMethod.unit || "xx",
              }));
            }
          } else {
            setUnitMaterials((prev) => ({
              ...prev,
              [rowId !== "new" ? rowId : "new"]: "xx",
            }));
          }
        },
        dropdownDataKey: "disposal_method",
      },
    }),
    []
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={{
        disposal_method: disposalMethodDropdown,
        treatment_country: countryDropdown,
      }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
      dynamicPlaceholders={dynamicPlaceholders}
      onNewItemFormReset={clearNewRowQtyUnit}
    
      comparisonConfig={{ categories: IMPACT_CATEGORIES }}
    />
  );
};

export default ProdSurplusTransportTable;

